import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { trackEvent } from "../utils/analytics";
import usePageTitle from "../hooks/usePageTitle";

import { useTranslation } from "react-i18next";
import authService from "../services/discordAuth";

function HomePage() {
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === "pt" ? "en" : "pt";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
  };

  usePageTitle(t("homepage.pageTitle"), t("homepage.pageDesc"));

  const [stats, setStats] = useState({
    totalBots: 0,
    totalVotes: 0,
    totalUsers: 0,
    avgRating: 0,
  });
  const [topBots, setTopBots] = useState([]);
  const [loading, setLoading] = useState(true);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

  // Buscar estatísticas e top bots
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/bots/stats/home`);
        if (!response.ok) throw new Error("Falha na resposta da API");

        const data = await response.json();

        if (data && data.stats) {
          setStats(data.stats);
        }
        if (data && data.topBots) {
          setTopBots(data.topBots);
        }
      } catch (err) {
        console.error("Erro ao buscar dados da homepage:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, [BACKEND_URL]);

  // Função para formatar números grandes
  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
    return num.toString();
  };

  // Badges de ranking
  const getRankBadge = (rank) => {
    const badges = ["🥇", "🥈", "🥉"];
    return `${badges[rank - 1]} #${rank}`;
  };

  return (
    <>
      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-background"></div>
        <div className="container">
          <div className="hero-content">
            {/* <div
              className="d-flex gap-2 mb-4"
              style={{ position: "relative", zIndex: 10 }}
            >
              <button
                className={`btn btn-sm ${i18n.language === "pt" ? "btn-primary" : "btn-outline-light"}`}
                onClick={() => {
                  i18n.changeLanguage("pt");
                  localStorage.setItem("language", "pt");
                }}
                style={{ fontSize: "0.7rem", padding: "2px 8px" }}
              >
                PT-BR
              </button>
              <button
                className={`btn btn-sm ${i18n.language === "en" ? "btn-primary" : "btn-outline-light"}`}
                onClick={() => {
                  i18n.changeLanguage("en");
                  localStorage.setItem("language", "en");
                }}
                style={{ fontSize: "0.7rem", padding: "2px 8px" }}
              >
                EN-US
              </button>
            </div> */}
            <h1 className="hero-title">
              {t("homepage.heroTitlePrefix")} <span className="gradient-text">{t("homepage.heroTitleGradient")}</span> {t("homepage.heroTitleSuffix")}
            </h1>
            <p className="hero-subtitle">{t("homepage.heroSubtitle")}</p>
            <div className="hero-buttons">
              <Link to="/bots" className="btn btn-large btn-primary" onClick={() => trackEvent("homepage_explore_click")}>
                <span>🤖</span> {t("homepage.exploreBots")}
              </Link>
              <Link
                to="/add-bot"
                className="btn btn-large btn-secondary text-white"
                onClick={(e) => {
                  if (!authService.isAuthenticated()) {
                    e.preventDefault();
                    authService.login();
                  } else {
                    trackEvent("homepage_add_bot_click");
                  }
                }}
              >
                <span>➕</span> {t("homepage.addMyBot")}
              </Link>
            </div>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">{formatNumber(stats.totalBots)}+</div>
                <div className="stat-label">{t("homepage.registeredBots")}</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{formatNumber(stats.totalVotes)}+</div>
                <div className="stat-label">{t("homepage.totalVotes")}</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{formatNumber(stats.totalUsers)}+</div>
                <div className="stat-label">{t("homepage.totalUsers")}</div>
              </div>
            </div>
          </div>
          <div className="hero-image">
            {loading ? (
              <div className="floating-card card-1">
                <div className="bot-preview">
                  <div className="bot-avatar-placeholder">⏳</div>
                  <div className="bot-info">
                    <h4>{t("homepage.loading")}</h4>
                    <p>⭐ 0.0 • 0 {t("homepage.votos")}</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {topBots[0] && (
                  <div className="floating-card card-1">
                    <div className="bot-preview">
                      <div className="bot-avatar-placeholder">
                        <img
                          src={topBots[0].avatar}
                          alt={topBots[0].name}
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                      <div className="bot-info">
                        <h4>{topBots[0].name}</h4>
                        <p>
                          ⭐ {topBots[0].rating.toFixed(1)} • {formatNumber(topBots[0].votes)} {t("homepage.votos")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {topBots[1] && (
                  <div className="floating-card card-2">
                    <div className="bot-preview">
                      <div className="bot-avatar-placeholder">
                        <img
                          src={topBots[1].avatar}
                          alt={topBots[1].name}
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                      <div className="bot-info">
                        <h4>{topBots[1].name}</h4>
                        <p>
                          ⭐ {topBots[1].rating.toFixed(1)} • {formatNumber(topBots[1].votes)} {t("homepage.votos")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {topBots[2] && (
                  <div className="floating-card card-3">
                    <div className="bot-preview">
                      <div className="bot-avatar-placeholder">
                        <img
                          src={topBots[2].avatar}
                          alt={topBots[2].name}
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                      <div className="bot-info">
                        <h4>{topBots[2].name}</h4>
                        <p>
                          ⭐ {topBots[2].rating.toFixed(1)} • {formatNumber(topBots[2].votes)} {t("homepage.votos")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              {t("homepage.whyChoosePrefix")} <span className="gradient-text">{t("homepage.whyChooseGradient")}</span>
              {t("homepage.whyChooseSuffix")}
            </h2>
            <p className="section-subtitle">{t("homepage.whySubtitle")}</p>
          </div>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🔍</div>
              <h3>{t("homepage.feature1Title")}</h3>
              <p>{t("homepage.feature1Desc")}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⭐</div>
              <h3>{t("homepage.feature2Title")}</h3>
              <p>{t("homepage.feature2Desc")}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>{t("homepage.feature3Title")}</h3>
              <p>{t("homepage.feature3Desc")}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🛡️</div>
              <h3>{t("homepage.feature4Title")}</h3>
              <p>{t("homepage.feature4Desc")}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3>{t("homepage.feature5Title")}</h3>
              <p>{t("homepage.feature5Desc")}</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💬</div>
              <h3>{t("homepage.feature6Title")}</h3>
              <p>{t("homepage.feature6Desc")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Top Bots Section */}
      <section id="bots" className="top-bots">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">
              {t("homepage.topBotsTitlePrefix")} <span className="gradient-text">{t("homepage.topBotsTitleGradient")}</span>
            </h2>
            <p className="section-subtitle">{t("homepage.topBotsSubtitle")}</p>
          </div>
          <div className="bots-grid">
            {loading ? (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "3rem",
                }}
              >
                <div className="loading-spinner">{t("homepage.loadingBots")}</div>
              </div>
            ) : topBots.length > 0 ? (
              topBots.map((bot) => (
                <div key={bot.id} className="bot-card">
                  <div className="bot-badge">{getRankBadge(bot.rank)}</div>
                  <div className="bot-avatar-large">
                    <img
                      src={bot.avatar}
                      alt={bot.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                  <h3 className="bot-name">{bot.name}</h3>
                  <p className="bot-description">{bot.description}</p>
                  <div className="bot-tags">
                    {bot.tags.map((tag, index) => (
                      <span key={index} className="tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="bot-stats">
                    <div className="stat">
                      <span>⭐</span>
                      <span>{bot.rating.toFixed(1)}</span>
                    </div>
                    <div className="stat">
                      <span>👥</span>
                      <span>{formatNumber(bot.servers)}</span>
                    </div>
                    <div className="stat">
                      <span>🗳️</span>
                      <span>{formatNumber(bot.votes)}</span>
                    </div>
                  </div>
                  <div className="bot-actions">
                    <Link
                      to={`https://discord.com/oauth2/authorize?client_id=${bot.id}&permissions=8&integration_type=0&scope=bot+applications.commands`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary btn-block"
                    >
                      {t("homepage.add")}
                    </Link>
                    <Link to={`/bots/${bot.id}`} className="btn btn-outline btn-block text-center d-flex align-items-center justify-content-center">
                      {t("homepage.seeMore")}
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "3rem",
                }}
              >
                <p>{t("homepage.noBots")}</p>
              </div>
            )}
          </div>
          <div className="section-footer">
            <Link to="/bots" className="btn btn-large btn-secondary">
              {t("homepage.viewAll")}
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="stats-section">
        <div className="container">
          <div className="stats-content">
            <div className="stats-text">
              <h2>
                {t("homepage.statsTitlePrefix")} <span className="gradient-text">{t("homepage.statsTitleGradient")}</span>
              </h2>
              <p>{t("homepage.statsSubtitle")}</p>
            </div>
            <div className="stats-grid">
              <div className="stat-box">
                <div className="stat-icon">🤖</div>
                <div className="stat-value">{formatNumber(stats.totalBots)}+</div>
                <div className="stat-text">{t("homepage.activeBots")}</div>
              </div>
              <div className="stat-box">
                <div className="stat-icon">👥</div>
                <div className="stat-value">{formatNumber(stats.totalUsers)}+</div>
                <div className="stat-text">{t("homepage.totalUsers")}</div>
              </div>
              <div className="stat-box">
                <div className="stat-icon">🗳️</div>
                <div className="stat-value">{formatNumber(stats.totalVotes)}+</div>
                <div className="stat-text">{t("homepage.totalVotes")}</div>
              </div>
              <div className="stat-box">
                <div className="stat-icon">🌟</div>
                <div className="stat-value">{stats.avgRating}</div>
                <div className="stat-text">{t("homepage.avgRatingLabel")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>{t("homepage.readyTitle")}</h2>
            <p>{t("homepage.readySubtitle")}</p>
            <div className="cta-buttons">
              <a
                href="/add-bot"
                className="btn btn-large btn-primary"
                onClick={(e) => {
                  if (!authService.isAuthenticated()) {
                    e.preventDefault();
                    authService.login();
                  }
                }}
              >
                {t("homepage.addMyBot")}
              </a>
              <a href="https://www.discord.gg/xK4r9HqKKf" className="btn btn-large btn-primary">
                {t("homepage.enterDiscord")}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default HomePage;
